import React from 'react';

import AvatarContainer from './AvatarContainer.jsx';
import CommentForm from './CommentForm.jsx';
import CommentFormWithAvatar from './CommentFormWithAvatar.jsx';
import UserLoginForm from './UserLoginForm.jsx';
import UserProfileForm from './UserProfileForm.jsx';
import UserModerationForm from './UserModerationForm.jsx';
import DeleteForm from './DeleteForm.jsx';

import CommentBody from './CommentBody.jsx';
import Number from './Number.jsx';
import Divider from './Divider';
import Timestamp from './Timestamp.jsx';

import './Comment.css';

import {
    EDITABLE_DURATION,
    VOTE_DOWN,
    VOTE_NONE,
    VOTE_UP,
    MAX_NEST_LEVEL,
    MODE_DEFAULT,
    MODE_EDIT,
    MODE_REPLY,
    MODE_DELETE,
    MODE_PROFILE,
    MODE_BLOCK,
    MODE_HISTORY
} from '../utils/constants';
import UserHistory from "./UserHistory";


class Comment extends React.Component {

    box = React.createRef();

    state = {
        mode: MODE_DEFAULT,
        voteInProgress: null,
        pinInProgress: false,
        contract: false,
        showLogin: false,
        optionsAreShowing: false
    };

    componentDidMount() {

        if (!this.props.scrolledToTlc && this.props.comment.hash === this.props.tlcCommentHash) {

            if (window.requestAnimationFrame) {

                return window.requestAnimationFrame(() => {
                    const rect = this.box.current.getBoundingClientRect();
                    this.props.scrollToTlc(rect.top);
                });
            }
            return setTimeout(() => {
                const rect = this.box.current.getBoundingClientRect();
                this.props.scrollToTlc(rect.top);
            }, 10);
        }
    }

    componentDidUpdate() {
        this.props.sendResizeEvent();
    }

    onChangeMode = ({ currentTarget: { value: mode } }) => {

        this.setState({ mode });
    };

    onToggleContract = e => {

        this.setState({
            contract: !this.state.contract
        });
    };

    afterCommentVote = () => {

        this.setState({ voteInProgress: null });
    };

    getUser = () => {

        const showLogin = !this.props.userId;

        return new Promise(resolve => {

            this.setState({ showLogin }, () => {

                this.props.getUser()
                    .catch(() => {})
                    .then(() => {

                        this.setState({ showLogin: false }, resolve);
                    });
            });
        });
    };

    onCommentVote = vote => {

        this.getUser()
            .then(() => {

                this.setState({ voteInProgress: vote }, () => {

                    this.props.commentVote(this.props.comment.id, vote)
                        .then(this.afterCommentVote)
                        .catch(this.afterCommentVote);
                });
        });
    };

    onUpVote = e => {

        this.getUser().then(() => this.onCommentVote(VOTE_UP));
    };

    onDownVote = e => {

        this.getUser().then(() => this.onCommentVote(VOTE_DOWN));
    };

    onFlag = e => {

        this.getUser().then(() => this.props.flagComment(this.props.comment.id));
    };

    onRemove = e => {

        this.getUser().then(() => this.props.removeComment(this.props.comment.id));
    };

    onPin = e => {

        this.getUser()
            .then(() => {

                this.setState({
                    pinInProgress: true
                }, () => {

                    this.props.pinComment(this.props.comment.id)
                        .then(() => {
                            this.setState({ pinInProgress: false });
                        });
                });
            });
    };

    onUnPin = e => {

        this.getUser()
            .then(() => {

                this.setState({
                    pinInProgress: true
                }, () => {

                    this.props.unpinComment(this.props.comment.id)
                        .then(() => {
                            this.setState({ pinInProgress: false });
                        });
                });
            });
    };

    onToggleOptionsAreShowing = e => {

        this.setState({ optionsAreShowing: !this.state.optionsAreShowing });
    };


    makeAvatarButton = mode => {

        return (
            <button
                type="button"
                value={mode}
                onClick={this.onChangeMode}
                className={this.state.mode === mode ? 'selected' : ''}
            >
                <span className="icon-settings" />
            </button>
        );
    };

    get authorButton() {

        const { owned, userId, userDisplayName } = this.props.comment;

        return (
            <button
                key="author"
                type="button"
                className={`author-button${this.state.mode === MODE_HISTORY ? ' selected' : ''}`}
                value={MODE_HISTORY}
                onClick={this.onChangeMode}
                disabled={!userId}
            >
                {`- `}
                {owned ? <span className="icon-person" /> : null}
                {userDisplayName || 'Anonymous'}
            </button>
        );
    }

    get replyButton() {

        const { mode } = this.state;
        const { userPageProfile: { comments } } = this.props;
        const limit = comments.length >= 100;

        return (
            <div key="reply" className="collapsible">
                <button
                    type="button"
                    value={mode === MODE_REPLY ? MODE_DEFAULT : MODE_REPLY}
                    onClick={this.onChangeMode}
                    className="reply-button"
                    disabled={limit}
                    title={limit ? 'You have reached the reply limit' : null}
                >
                    <span className="icon-question_answer words" />
                    {mode === MODE_REPLY ? 'cancel' : 'reply'}
                </button>
            </div>
        );
    };

    get upVoteButton() {

        const { voteInProgress } = this.state;
        const {
            comment,
            commentVotes,
            userPageProfile: {
                comments,
                commentsVotedOn,
                commentVoteVersions
            }
        } = this.props;

        const commentId = comment.id;

        const selected = (
            commentsVotedOn[commentId] === VOTE_UP && ![VOTE_NONE, VOTE_DOWN].includes(voteInProgress)
        ) || voteInProgress === VOTE_UP;

        const limitGeneral = Object.keys(commentsVotedOn).length >= 100;
        const limitSpecific = parseInt(commentVoteVersions[commentId] || '0') >= 9;

        return (
            <div key="up-vote" className="collapsible">
                <button
                    type="button"
                    value={commentId}
                    className={`up-vote-button${selected ? ' selected' : ''}`}
                    onClick={this.onUpVote}
                    disabled={comments[commentId] || !!voteInProgress || limitGeneral || limitSpecific}
                    title={limitGeneral ? 'You have reached the vote limit' : limitSpecific ? 'You may only vote on the same comment a limited number of times' : null}
                >
                    <span className="icon-thumb_up" /> <Number value={commentVotes.up[commentId]} />
                </button>
            </div>
        );
    };

    get downVoteButton() {

        const { voteInProgress } = this.state;
        const {
            comment,
            userPageProfile: {
                commentsVotedOn,
                commentVoteVersions
            }
        } = this.props;

        const commentId = comment.id;

        const selected = (
            commentsVotedOn[commentId] === VOTE_DOWN && ![VOTE_NONE, VOTE_UP].includes(voteInProgress)
        ) || voteInProgress === VOTE_DOWN;

        const limitGeneral = Object.keys(commentsVotedOn).length >= 100;
        const limitSpecific = parseInt(commentVoteVersions[commentId] || '0') >= 9;

        return (
            <div key="down-vote" className="collapsible">
                <button
                    type="button"
                    value={commentId}
                    className={`down-vote-button${selected ? ' selected' : ''}`}
                    onClick={this.onDownVote}
                    disabled={!!voteInProgress || limitGeneral || limitSpecific}
                    title={limitGeneral ? 'You have reached the vote limit' : limitSpecific ? 'You may only vote on the same comment a limited number of times' : null}
                >
                    <span className="icon-thumb_down" />
                </button>
            </div>
        );
    };

    get flagButton() {

        const { userPageProfile: { flaggedComments } } = this.props;
        const selected = !!flaggedComments[this.props.comment.id];
        const limit = Object.keys(flaggedComments).length >= 100;

        return (
            <div key="flag" className="hidden collapsible">
                <button
                    type="button"
                    className={`flag-button${selected ? ' selected' : ''}`}
                    onClick={this.onFlag}
                    disabled={selected || limit}
                    title={limit ? 'You have reached the flag limit' : null}
                >
                    <span className="icon-flag2 words" />
                    flag{selected ? 'ged' : ''}
                </button>
            </div>
        );
    };

    get editButton() {

        const {
            userPageProfile: {
                comments
            },
        } = this.props;
        const { mode } = this.state;
        const editableUntil = parseInt(this.props.comment.createdAt) + EDITABLE_DURATION;
        const limit = parseInt(comments[this.props.comment.id]) > 9;

        return (
            <div key="edit" className="hidden collapsible">
                <button
                    type="button"
                    value={mode === MODE_EDIT ? MODE_DEFAULT : MODE_EDIT}
                    onClick={this.onChangeMode}
                    className="edit-button"
                    disabled={limit}
                    title={limit ? 'You may only edit a limited number of times' : null}
                >
                    <span className="icon-chat_bubble words"/>
                    {mode === MODE_EDIT ? 'cancel ' : 'edit '}
                    (<Timestamp value={editableUntil} /> left)
                </button>
            </div>
        );
    };

    get deleteButton() {

        const { userPageProfile: { deletedComments } } = this.props;
        const { mode } = this.state;
        const limit = Object.keys(deletedComments).length >= 10000;

        return (
            <div key="delete" className="hidden collapsible">
                <button
                    type="button"
                    value={mode === MODE_DELETE ? MODE_DEFAULT : MODE_DELETE}
                    onClick={this.onChangeMode}
                    className="remove-button"
                    disabled={limit}
                    title={limit ? 'You have reached the delete limit' : null}

                >
                    <span className="icon-delete words"/>
                    {mode === MODE_DELETE ? 'cancel ' : 'delete '}
                </button>
            </div>
        );
    };

    get pinButton() {


        const { pinInProgress } = this.state;
        const {
            comment,
            page: {
                pinnedComments
            }
        } = this.props;

        const commentId = comment.id;

        const selected = pinnedComments[commentId] || pinInProgress;

        return (
            <div key="pin" className="hidden collapsible">
                <button
                    type="button"
                    value={commentId}
                    className={`pin-button${selected ? ' selected' : ''}`}
                    onClick={pinnedComments[commentId] ? this.onUnPin : this.onPin}
                    disabled={pinInProgress}
                    title="Pin comments to keep them at the top"
                >
                    <span className="icon-star" /> {pinnedComments[commentId] ? 'unpin' : 'pin'}
                </button>
            </div>
        );

    }


    get commentBody() {

        return (
            <CommentBody value={this.props.comment.body} />
        );
    }

    get replies() {

        if (this.state.contract) {

            const count = this.constructor.countCommentsAndReplies(this.props.comment);
            const replyDisplay = `repl${count === 1 ? 'y' : 'ies'}`;

            return (
                <button
                    type="button"
                    className="expand-button"
                    onClick={this.onToggleContract}
                >
                    <span className="icon-unfold_more"/> Show {count} {replyDisplay}
                </button>
            );
        }

        return this.props.renderComments(this.props.comment.replies);
    }

    get optionsButton() {

        return (
            <button
                key="options"
                type="button"
                className="options-button"
                onClick={this.onToggleOptionsAreShowing}
            >
                <span className={`icon-unfold_${this.state.optionsAreShowing ? 'less' : 'more'}`} />
            </button>
        );
    }

    get actions() {

        const {
            userIsModerator,
            editableComments,
            comment: {
                id,
                owned
            }
        } = this.props;

        const nestLevel = id.split('/').length;
        const actions = [];

        if (this.props.page.pinnedComments[this.props.comment.id]) {
            actions.push(<span key="is-pinned" className="icon-star pin" />);
        }

        actions.push(
            this.authorButton,
            <Divider key="before-timestamp" />,
            <Timestamp key="timestamp" value={this.props.comment.createdAt} />,
            this.optionsButton,
            <Divider key="before-upvote" />,
            this.upVoteButton
        );

        if (owned) {

            if (nestLevel < MAX_NEST_LEVEL) {

                actions.push(
                    <Divider key="before-reply" />,
                    this.replyButton
                );
            }

            if (!!editableComments[id]) {

                actions.push(
                    <Divider key="before-edit" hidden />,
                    this.editButton
                );
            }

            actions.push(
                <Divider key="before-delete" hidden />,
                this.deleteButton
            );

        } else {

            actions.push(
                <Divider key="before-downvote" />,
                this.downVoteButton,
            );
            if (nestLevel < MAX_NEST_LEVEL) {

                actions.push(
                    <Divider key="before-reply" />,
                    this.replyButton
                );
            }
            if (userIsModerator) {
                actions.push(
                    <Divider key="before-delete" hidden />,
                    this.deleteButton
                );
            } else {
                actions.push(
                    <Divider key="before-flag" hidden />,
                    this.flagButton
                );
            }
        }

        if (userIsModerator) {

            actions.push(
                <Divider key="before-pin" hidden />,
                this.pinButton
            );
        }

        return actions;
    }

    get content() {

        if (!this.props.comment.body) {
            return (
                <div className="deleted-comment">
                    <div className="comment-content">
                        <div className="body">
                            <p>
                                This comment has been deleted.
                            </p>
                        </div>
                    </div>
                    <div className="children">
                        <div className="right">
                            <div className={`level level-${this.props.comment.id.split('/').length}`}>
                                {this.replies}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }


        const props = {
            image: this.props.comment.userPhotoURL,
            button: this.props.userIsModerator
            && this.props.comment.userId !== this.props.userId ? this.makeAvatarButton(MODE_BLOCK) : null,
            right: (
                <div className="comment-content">
                    <div className={`body${this.props.userPageProfile.flaggedComments[this.props.comment.id] ? ' flagged' : ''}`}>
                        {this.commentBody}
                    </div>

                    <div className={`actions${this.state.optionsAreShowing ? ' options' : ''}`}>
                        {this.actions}
                    </div>
                </div>
            )
        };

        const children = [];

        switch(this.state.mode) {
            case MODE_EDIT:
                Object.assign(props, {
                    image: this.props.userPhotoURL,
                    button: this.makeAvatarButton(MODE_PROFILE),
                    right: (
                        <CommentForm
                            {...this.props}
                            body={this.props.comment.body}
                            mode={this.state.mode}
                            commentId={this.props.comment.id}
                            onCancel={this.onChangeMode}
                            onCancelValue={MODE_DEFAULT}
                        />
                    )
                });
                break;
            case MODE_REPLY:
                children.push(

                    <CommentFormWithAvatar
                        {...this.props}
                        mode={this.state.mode}
                        commentId={this.props.comment.id}
                        onCancel={this.onChangeMode}
                        onCancelValue={MODE_DEFAULT}
                    />
                );
                break;
            case MODE_DELETE:
                // TODO
                children.push(
                    <div className="confirm-deletion">
                        <DeleteForm
                            {...this.props}
                            closeValue={MODE_DEFAULT}
                            close={this.onChangeMode}
                            onRemove={this.onRemove}
                        />
                    </div>
                );
                break;
            case MODE_PROFILE:
                Object.assign(props, {
                    image: this.props.userPhotoURL,
                    button: this.makeAvatarButton(MODE_PROFILE),
                    right: (
                        <UserProfileForm
                            {...this.props}
                            close={this.onChangeMode}
                            closeValue={MODE_REPLY}
                        />
                    )
                });
                break;
            case MODE_BLOCK:
                Object.assign(props, {
                    button: this.makeAvatarButton(MODE_BLOCK),
                    right: (
                        <UserModerationForm
                            {...this.props}
                            close={this.onChangeMode}
                            closeValue={MODE_DEFAULT}
                            commentId={this.props.comment.id}
                        />
                    )
                });
                break;
            case MODE_HISTORY:
                children.push(
                    <div className="show-user-history">
                        <UserHistory
                            {...this.props}
                            projectId={this.props.projectId}
                            userId={this.props.comment.userId}
                            userDisplayName={this.props.comment.userDisplayName}
                            closeValue={MODE_DEFAULT}
                            close={this.onChangeMode}
                        />
                    </div>
                );
                break;
        }

        children.push(this.replies);

        return (
            <AvatarContainer {...props}>
                {children.map((c, i) => <div className={`level level-${this.props.comment.id.split('/').length}`} key={i}>{c}</div>)}
            </AvatarContainer>
        );
    }

    render() {

        if (this.state.showLogin && this.props.onUser) {
            return (
                <UserLoginForm
                    {...this.props}
                    close={this.props.onUser.reject}
                    authUiConfig={this.props.authUiConfig}
                />
            );
        }

        const contractStyle =
            (this.props.comment.replies.length === 0
                || !this.props.comment.body
                || this.state.contract
            ) ? { display: 'none' } : {};

        return (
            <div id={this.props.comment.hash} className="comment" ref={this.box}>

                <button
                    type="button"
                    className="contract-button"
                    onClick={this.onToggleContract}
                    style={contractStyle}
                >
                    <span className="line" />
                    <small>
                        <span className="icon-unfold_less" />
                        <br />
                        {this.constructor.countCommentsAndReplies(this.props.comment)}
                    </small>
                </button>

                <div className="content">

                    {this.content}

                </div>

            </div>
        );
    }

    static countCommentsAndReplies(comment) {

        if (!comment.body && !comment.replies.length) {
            return 0;
        }

        const replies = comment.replies.filter(comment => (!!comment.body || (!comment.body && comment.replies.length > 0)));

        let count = replies.length;
        if (count === 0) {
            return count;
        }
        if (!comment.body) {
            count--;
        }
        replies.forEach(comment => {

            count+= this.countCommentsAndReplies(comment);
        });

        return count;
    }
}

export default Comment;


// WEBPACK FOOTER //
// ./src/components/Comment.jsx